import { isEmpty } from "@/utils/var.check";
import resourceApi from "@/apis/resource.api";
import { reactive } from "vue";
import RandomUtils from "@utils/random.utils.ts";

export default {
  state: reactive({
    contentMapTaggingsByTagId: {}, // used for CM tagging
    subjectAndLevelTaggingByTagId: {}, // used for subject-level tagging
    hiddenContentMapTaggingsByTagId: {},
  }),

  /**
   * getter method
   */
  getSubjectAndLevelTaggings() {
    return Object.values(this.state.subjectAndLevelTaggingByTagId);
  },
  getContentMapTaggingByTagId(tagId) {
    return this.state.contentMapTaggingsByTagId[tagId];
  },
  getSubjectAndLevelTaggingsByResource(resourceUuid) {
    const keys = Object.keys(this.state.subjectAndLevelTaggingByTagId);
    const filteredKeys = keys.filter((key) => key.startsWith(resourceUuid));
    return filteredKeys.map((key) => {
      return this.state.subjectAndLevelTaggingByTagId[key];
    });
  },
  getContentMapTaggingsByResource(resourceUuid) {
    const keys = Object.keys(this.state.contentMapTaggingsByTagId);
    const filteredKeys = keys.filter((key) => key.startsWith(resourceUuid));
    return filteredKeys.map((key) => {
      return this.state.contentMapTaggingsByTagId[key];
    });
  },
  getHiddenContentMapTaggingsByResource(resourceUuid) {
    const keys = Object.keys(this.state.hiddenContentMapTaggingsByTagId);
    const filteredKeys = keys.filter((key) => key.startsWith(resourceUuid));
    return filteredKeys.map((key) => {
      return this.state.hiddenContentMapTaggingsByTagId[key];
    });
  },
  /**
   * mutation method
   */
  addContentMapTagging(payload) {
    this.state.contentMapTaggingsByTagId[payload.tagId] = payload;
  },
  addSubjectLevelTagging(payload) {
    this.state.subjectAndLevelTaggingByTagId[payload.tagId] = payload;
  },
  addHiddenContentMapTagging(payload) {
    this.state.hiddenContentMapTaggingsByTagId[payload.tagId] = payload;
  },
  removeSubjectLevelTagging(tagId) {
    delete this.state.subjectAndLevelTaggingByTagId[tagId];
  },
  removeContentMapTagging(tagId) {
    delete this.state.contentMapTaggingsByTagId[tagId];
  },
  addEditingResourceTaggings(resourceTaggings) {
    if (isEmpty(resourceTaggings)) return;
    resourceTaggings.forEach((resourceTagging) => {
      this.addContentMapTagging(resourceTagging);
    });
  },
  resetFullyTaggedStatusByOldData(data) {
    const oldTagging = data.oldTagging;
    const store = data.store;

    const oldContentMapMetadata = oldTagging.contentMapMetadata;
    const contentMap = store.getContentMap(oldTagging.contentMapMetadata.uuid);

    Object.keys(contentMap.subjects).forEach((subjectId) => {
      const subject = store.getSubject(subjectId);
      subject.isFullyTagged = false;
      subject.levelAndStreams.forEach((levelAndStream) => {
        const levelAndStreamIds = Object.keys(contentMap.subjects[subjectId].levelAndStreams);
        if (levelAndStreamIds.includes(levelAndStream.id.toString())) {
          levelAndStream.isFullyTagged = false;
          levelAndStream.contentMaps.forEach((contentMap) => {
            if (contentMap.uuid === oldContentMapMetadata.uuid) {
              contentMap.isFullyTagged = false;
            }
          });
        }
      });
    });
  },
  resetTaggingFullyTaggedStatus(data) {
    this.resetFullyTaggedStatusByOldData({
      store: data.store,
      oldTagging: data.oldTagging,
    });
  },
  updateTaggingFullyTaggedStatus(data) {
    const newTagging = data.newTagging;
    const store = data.store;

    const newContentMapMetadata = newTagging.contentMapMetadata;
    const contentMap = store.getContentMap(newTagging.contentMapMetadata.uuid);

    Object.keys(contentMap.subjects).forEach((subjectId) => {
      const subject = store.getSubject(subjectId);
      subject.isFullyTagged = true;
      subject.levelAndStreams.forEach((levelAndStream) => {
        levelAndStream.isFullyTagged = true;
        const levelAndStreamIds = Object.keys(contentMap.subjects[subjectId].levelAndStreams);
        if (levelAndStreamIds.includes(levelAndStream.id.toString())) {
          levelAndStream.contentMaps.forEach((contentMap) => {
            if (contentMap.isFullyTagged) return;

            contentMap.isFullyTagged = true;
            if (contentMap.uuid !== newContentMapMetadata.uuid) {
              contentMap.isFullyTagged = false;
              levelAndStream.isFullyTagged = false;
            }
          });
        } else {
          levelAndStream.isFullyTagged = false;
        }

        if (!levelAndStream.isFullyTagged) {
          subject.isFullyTagged = false;
        }
      });
    });
  },

  /**
   * action method
   */
  fetchResourceTaggings({ uuid, resourceType, resourceStatus, includeExpired }) {
    return new Promise((resolve, reject) => {
      resourceApi.fetchResourceTaggings(uuid, resourceType, resourceStatus, includeExpired).then(
        //OK
        (response) => {
          if (response.code !== "ok") {
            reject(response.errorMap);
            return;
          }
          // process data
          this.processResourceTaggings(response.data, uuid);
          resolve();
        },
        (error) => {
          reject(error);
        },
      );
    });
  },
  processResourceTaggings(data, uuid) {
    if (isEmpty(data)) {
      return;
    }
    // add resource taggings
    this.processContentMapTagging(data.resourceTaggingContentMapList, uuid);
    this.processSubjectLevelTagging(data.resourceTaggingSubjectLevelList, uuid);
    this.processHiddenContentMapTagging(data.hiddenResourceTaggingContentMapList, uuid);
  },
  processSubjectLevelTagging(resourceTaggingSubjectLevelList, uuid) {
    this.state.subjectAndLevelTaggingByTagId = {};

    resourceTaggingSubjectLevelList.forEach((resourceTaggingSubjectLevel) => {
      const tagId = RandomUtils.randomId();
      resourceTaggingSubjectLevel.tagId = uuid.concat("_", tagId.toString());
      this.addSubjectLevelTagging(resourceTaggingSubjectLevel);
    });
  },
  processContentMapTagging(resourceTaggingContentMapList, uuid) {
    this.state.contentMapTaggingsByTagId = {};

    resourceTaggingContentMapList.forEach((resourceTaggingContentMap) => {
      const tagId = RandomUtils.randomId();
      resourceTaggingContentMap.tagId = uuid.concat("_", tagId.toString());
      this.addContentMapTagging(resourceTaggingContentMap);
    });
  },
  processHiddenContentMapTagging(hiddenResourceTaggingContentMapList, uuid) {
    this.state.hiddenContentMapTaggingsByTagId = {};

    hiddenResourceTaggingContentMapList.forEach((hiddenResourceTaggingContentMap) => {
      const tagId = RandomUtils.randomId();
      hiddenResourceTaggingContentMap.tagId = uuid.concat("_", tagId.toString());
      this.addHiddenContentMapTagging(hiddenResourceTaggingContentMap);
    });
  },
  clearResourceTaggings() {
    this.destroy();
  },
  clearResourceTaggingsByResource(resourceUuid) {
    let keys = Object.keys(this.state.subjectAndLevelTaggingByTagId);
    let filteredKeys = keys.filter((key) => key.startsWith(resourceUuid));
    filteredKeys.forEach((key) => {
      delete this.state.subjectAndLevelTaggingByTagId[key];
    });

    keys = Object.keys(this.state.contentMapTaggingsByTagId);
    filteredKeys = keys.filter((key) => key.startsWith(resourceUuid));
    filteredKeys.forEach((key) => {
      delete this.state.contentMapTaggingsByTagId[key];
    });

    keys = Object.keys(this.state.hiddenContentMapTaggingsByTagId);
    filteredKeys = keys.filter((key) => key.startsWith(resourceUuid));
    filteredKeys.forEach((key) => {
      delete this.state.hiddenContentMapTaggingsByTagId[key];
    });
  },
  destroy() {
    this.state = {
      contentMapTaggingsByTagId: {},
      subjectAndLevelTaggingByTagId: {},
      hiddenContentMapTaggingsByTagId: {},
    };
  },
};
